import { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PrintReadyModal from './PrintReadyModal';

interface ShareDesignModalProps {
  darkMode: boolean;
  onClose: () => void;
  selectedStyle: string;
  floorPlanConfig: any;
  onCopyLink: () => void;
}

// ── Room breakdown helpers ──────────────────────────────────────────────────
interface RoomBreakdown {
  name: string;
  icon: string;
  size: string;
  description: string;
  color: string;
}

const getRoomBreakdown = (config: any, style: string): RoomBreakdown[] => {
  const totalSqft = config.unit === 'sqm'
    ? Math.round(config.area * 10.764)
    : config.area;
  const totalSqm = config.unit === 'sqft'
    ? Math.round(config.area * 0.0929)
    : config.area;

  const fmt = (sqm: number) =>
    config.unit === 'sqft'
      ? `${Math.round(sqm * 10.764)} sqft`
      : `${sqm} m²`;

  const rooms: RoomBreakdown[] = [];

  // Living Room ~18%
  const livingM2 = Math.round(totalSqm * 0.18);
  rooms.push({
    name: 'Living Room',
    icon: 'ri-sofa-line',
    size: fmt(livingM2),
    description: style === 'Minimalist'
      ? 'Open plan with clean lines and minimal furniture'
      : style === 'Mediterranean'
      ? 'Arched openings, warm tones, central gathering space'
      : 'Open flow to dining, natural light focus',
    color: 'from-teal-500 to-cyan-500',
  });

  // Master Bedroom ~14%
  const masterM2 = Math.round(totalSqm * 0.14);
  rooms.push({
    name: 'Master Bedroom',
    icon: 'ri-hotel-bed-line',
    size: fmt(masterM2),
    description: totalSqm > 120
      ? 'En-suite bathroom + walk-in closet included'
      : 'En-suite bathroom, private retreat',
    color: 'from-violet-500 to-purple-500',
  });

  // Additional Bedrooms
  const extraBeds = config.bedrooms - 1;
  if (extraBeds > 0) {
    const bedM2 = Math.round((totalSqm * 0.09));
    rooms.push({
      name: `${extraBeds} Additional Bedroom${extraBeds > 1 ? 's' : ''}`,
      icon: 'ri-home-2-line',
      size: `${fmt(bedM2)} each`,
      description: extraBeds > 1
        ? 'Shared hallway access, flexible use as guest or office'
        : 'Flexible use — guest room or home office',
      color: 'from-indigo-500 to-blue-500',
    });
  }

  // Kitchen ~10%
  const kitchenM2 = Math.round(totalSqm * 0.10);
  rooms.push({
    name: 'Kitchen',
    icon: 'ri-knife-line',
    size: fmt(kitchenM2),
    description: config.kitchenType === 'open'
      ? 'Open-plan island kitchen, flows into living/dining'
      : 'Closed kitchen with pantry and dedicated prep zone',
    color: 'from-orange-500 to-amber-500',
  });

  // Bathrooms ~7% each
  const bathM2 = Math.round(totalSqm * 0.07);
  rooms.push({
    name: `${config.bathrooms} Bathroom${config.bathrooms > 1 ? 's' : ''}`,
    icon: 'ri-drop-line',
    size: `${fmt(bathM2)} each`,
    description: config.bathrooms > 1
      ? '1 en-suite master bath + shared bathroom(s)'
      : 'Full bathroom with walk-in shower',
    color: 'from-sky-500 to-blue-400',
  });

  // Dining Room ~9%
  const diningM2 = Math.round(totalSqm * 0.09);
  rooms.push({
    name: 'Dining Room',
    icon: 'ri-restaurant-line',
    size: fmt(diningM2),
    description: config.kitchenType === 'open'
      ? 'Integrated with kitchen and living area'
      : 'Separate formal dining space',
    color: 'from-rose-500 to-pink-500',
  });

  // Extras
  const extras: string[] = config.extras || [];
  if (extras.includes('Home Office')) {
    rooms.push({
      name: 'Home Office',
      icon: 'ri-computer-line',
      size: fmt(Math.round(totalSqm * 0.07)),
      description: 'Quiet zone near entrance, built-in shelving',
      color: 'from-emerald-500 to-green-500',
    });
  }
  if (extras.includes('Garage')) {
    rooms.push({
      name: 'Garage',
      icon: 'ri-car-line',
      size: fmt(Math.round(totalSqm * 0.12)),
      description: 'Double garage with direct interior access',
      color: 'from-gray-500 to-slate-500',
    });
  }
  if (extras.includes('Laundry Room')) {
    rooms.push({
      name: 'Laundry Room',
      icon: 'ri-t-shirt-line',
      size: fmt(Math.round(totalSqm * 0.04)),
      description: 'Utility room with washer/dryer hookups',
      color: 'from-cyan-500 to-teal-400',
    });
  }
  if (extras.includes('Balcony') || extras.includes('Terrace')) {
    rooms.push({
      name: extras.includes('Terrace') ? 'Terrace' : 'Balcony',
      icon: 'ri-sun-line',
      size: fmt(Math.round(totalSqm * 0.06)),
      description: 'Outdoor living extension with scenic views',
      color: 'from-yellow-500 to-orange-400',
    });
  }

  return rooms;
};

// ── Component ───────────────────────────────────────────────────────────────
const ShareDesignModal = ({
  darkMode,
  onClose,
  selectedStyle,
  floorPlanConfig,
  onCopyLink,
}: ShareDesignModalProps) => {
  const [isDownloadingImg, setIsDownloadingImg] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [showPrintReady, setShowPrintReady] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const roomBreakdown = getRoomBreakdown(floorPlanConfig, selectedStyle);

  const getStyleThumbnail = (styleName: string) => {
    const styleImages: { [key: string]: string } = {
      'Colonial': 'https://public.readdy.ai/ai/img_res/a3cefb49c2f8f1a27bd0118ff705a3c0.jpg',
      'Victorian': 'https://readdy.ai/api/search-image?query=ornate%20victorian%20house%20with%20decorative%20trim%20bay%20windows%20turret%20tower%20pastel%20colors%20intricate%20architectural%20details%20beautiful%20garden%20clear%20sky&width=600&height=400&seq=victorian-share-001&orientation=landscape',
      'Craftsman': 'https://readdy.ai/api/search-image?query=craftsman%20style%20bungalow%20with%20exposed%20beams%20wide%20front%20porch%20natural%20wood%20siding%20stone%20accents%20warm%20inviting%20architecture%20beautiful%20landscaping&width=600&height=400&seq=craftsman-share-001&orientation=landscape',
      'Tudor': 'https://readdy.ai/api/search-image?query=tudor%20style%20house%20with%20half%20timbered%20facade%20steeply%20pitched%20roof%20decorative%20woodwork%20brick%20and%20stucco%20exterior%20charming%20english%20architecture%20beautiful%20garden&width=600&height=400&seq=tudor-share-001&orientation=landscape',
      'Minimalist': 'https://readdy.ai/api/search-image?query=minimalist%20modern%20house%20with%20clean%20lines%20white%20walls%20large%20windows%20simple%20geometric%20shapes%20sleek%20contemporary%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=600&height=400&seq=minimalist-share-001&orientation=landscape',
      'Contemporary': 'https://readdy.ai/api/search-image?query=contemporary%20house%20with%20mixed%20materials%20glass%20steel%20wood%20asymmetrical%20design%20innovative%20architecture%20beautiful%20modern%20landscaping%20clear%20blue%20sky&width=600&height=400&seq=contemporary-share-001&orientation=landscape',
      'Industrial': 'https://readdy.ai/api/search-image?query=industrial%20style%20house%20with%20exposed%20concrete%20metal%20accents%20large%20windows%20raw%20materials%20urban%20architecture%20beautiful%20minimal%20landscaping&width=600&height=400&seq=industrial-share-001&orientation=landscape',
      'Scandinavian': 'https://readdy.ai/api/search-image?query=scandinavian%20style%20house%20with%20light%20wood%20facade%20simple%20clean%20design%20large%20windows%20natural%20materials%20nordic%20architecture%20beautiful%20minimal%20garden&width=600&height=400&seq=scandinavian-share-001&orientation=landscape',
      'Mediterranean': 'https://readdy.ai/api/search-image?query=mediterranean%20villa%20with%20terracotta%20roof%20stucco%20walls%20arched%20windows%20courtyard%20warm%20earth%20tones%20beautiful%20landscaping%20palm%20trees%20clear%20sky&width=600&height=400&seq=mediterranean-share-001&orientation=landscape',
      'Farmhouse': 'https://readdy.ai/api/search-image?query=modern%20farmhouse%20with%20white%20board%20and%20batten%20siding%20black%20windows%20metal%20roof%20front%20porch%20rustic%20charm%20beautiful%20landscaping%20clear%20sky&width=600&height=400&seq=farmhouse-share-001&orientation=landscape',
      'Ranch': 'https://readdy.ai/api/search-image?query=ranch%20style%20house%20with%20long%20low%20profile%20attached%20garage%20horizontal%20lines%20simple%20design%20beautiful%20landscaping%20wide%20open%20yard%20clear%20sky&width=600&height=400&seq=ranch-share-001&orientation=landscape',
      'Cape Cod': 'https://readdy.ai/api/search-image?query=cape%20cod%20style%20house%20with%20steep%20roof%20dormer%20windows%20shingle%20siding%20symmetrical%20facade%20traditional%20new%20england%20architecture%20beautiful%20garden&width=600&height=400&seq=capecod-share-001&orientation=landscape',
      'Tiny Home': 'https://readdy.ai/api/search-image?query=modern%20tiny%20house%20with%20efficient%20design%20large%20windows%20wood%20siding%20compact%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky%20sustainable%20living&width=600&height=400&seq=tinyhome-share-001&orientation=landscape',
      'Container': 'https://readdy.ai/api/search-image?query=shipping%20container%20house%20with%20industrial%20design%20large%20windows%20modern%20conversion%20innovative%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=600&height=400&seq=container-share-001&orientation=landscape',
      'A-Frame': 'https://readdy.ai/api/search-image?query=a-frame%20cabin%20with%20steep%20triangular%20roof%20large%20front%20windows%20wood%20siding%20mountain%20retreat%20architecture%20beautiful%20natural%20landscaping%20clear%20sky&width=600&height=400&seq=aframe-share-001&orientation=landscape',
      'Dome': 'https://readdy.ai/api/search-image?query=geodesic%20dome%20house%20with%20unique%20spherical%20design%20modern%20materials%20innovative%20architecture%20beautiful%20landscaping%20clear%20sky%20futuristic%20sustainable%20home&width=600&height=400&seq=dome-share-001&orientation=landscape',
    };
    return styleImages[styleName] || 'https://readdy.ai/api/search-image?query=modern%20architectural%20house%20design%20beautiful%20exterior%20professional%20rendering%20clean%20simple%20background&width=600&height=400&seq=default-share-001&orientation=landscape';
  };

  const getSelectedExtras = () => (floorPlanConfig.extras || []).slice(0, 5);

  // ── Download as Image ──
  const handleDownloadImage = async () => {
    if (!cardRef.current) return;
    setIsDownloadingImg(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: darkMode ? '#111827' : '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      const link = document.createElement('a');
      link.download = `design-summary-${(selectedStyle || 'my-design').toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Image download failed:', err);
    } finally {
      setIsDownloadingImg(false);
    }
  };

  // ── Download as PDF ──
  const handleDownloadPdf = async () => {
    if (!cardRef.current) return;
    setIsDownloadingPdf(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const usableW = pageW - margin * 2;

      // ── Page 1: Summary card ──
      const cardAspect = canvas.height / canvas.width;
      const cardH = Math.min(usableW * cardAspect, pageH - margin * 2);
      pdf.addImage(imgData, 'PNG', margin, margin, usableW, cardH);

      // ── Page 2: Room-by-room breakdown ──
      pdf.addPage();
      let y = margin;

      // Header bar
      pdf.setFillColor(20, 184, 166); // teal-500
      pdf.roundedRect(margin, y, usableW, 14, 3, 3, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Room-by-Room Breakdown', margin + 4, y + 9.5);
      y += 20;

      // Sub-header
      pdf.setTextColor(100, 116, 139);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      const subtitle = `${selectedStyle || 'Custom'} Style  ·  ${floorPlanConfig.bedrooms} Bed  ·  ${floorPlanConfig.bathrooms} Bath  ·  ${floorPlanConfig.area} ${floorPlanConfig.unit}`;
      pdf.text(subtitle, margin, y);
      y += 8;

      // Divider
      pdf.setDrawColor(226, 232, 240);
      pdf.line(margin, y, pageW - margin, y);
      y += 6;

      // Room rows
      const rowH = 22;
      const colW = (usableW - 6) / 2;

      roomBreakdown.forEach((room, idx) => {
        const col = idx % 2;
        const x = margin + col * (colW + 6);

        if (col === 0 && idx > 0) y += rowH + 4;

        // Card background
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(x, y, colW, rowH, 2, 2, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.roundedRect(x, y, colW, rowH, 2, 2, 'S');

        // Accent bar
        pdf.setFillColor(20, 184, 166);
        pdf.roundedRect(x, y, 3, rowH, 1, 1, 'F');

        // Room name
        pdf.setTextColor(15, 23, 42);
        pdf.setFontSize(8.5);
        pdf.setFont('helvetica', 'bold');
        pdf.text(room.name, x + 7, y + 7);

        // Size badge
        pdf.setFillColor(204, 251, 241);
        pdf.roundedRect(x + 7, y + 9, colW - 14, 5, 1, 1, 'F');
        pdf.setTextColor(15, 118, 110);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.text(room.size, x + 9, y + 13);

        // Description
        pdf.setTextColor(100, 116, 139);
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'normal');
        const descLines = pdf.splitTextToSize(room.description, colW - 10);
        pdf.text(descLines[0] || '', x + 7, y + 19);
      });

      // Ensure last row is accounted for
      if (roomBreakdown.length % 2 !== 0) y += rowH + 4;
      else y += rowH + 4;

      y += 8;

      // Extras section
      const extras: string[] = floorPlanConfig.extras || [];
      if (extras.length > 0) {
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, y, pageW - margin, y);
        y += 6;

        pdf.setTextColor(15, 23, 42);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Selected Features', margin, y);
        y += 6;

        let tagX = margin;
        extras.forEach((extra) => {
          const tagW = pdf.getTextWidth(extra) + 8;
          if (tagX + tagW > pageW - margin) {
            tagX = margin;
            y += 8;
          }
          pdf.setFillColor(204, 251, 241);
          pdf.roundedRect(tagX, y - 4, tagW, 6, 2, 2, 'F');
          pdf.setTextColor(15, 118, 110);
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'normal');
          pdf.text(extra, tagX + 4, y);
          tagX += tagW + 4;
        });
        y += 10;
      }

      // Footer
      y = pageH - margin - 6;
      pdf.setDrawColor(226, 232, 240);
      pdf.line(margin, y, pageW - margin, y);
      y += 4;
      pdf.setTextColor(148, 163, 184);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Generated by Master Architect · AI-Powered Design Platform', margin, y);
      pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageW - margin, y, { align: 'right' });

      pdf.save(`floor-plan-${(selectedStyle || 'design').toLowerCase().replace(/\s+/g, '-')}.pdf`);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const projectTitle = selectedStyle ? `${selectedStyle} Design` : 'My Design';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-300`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div>
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Share Your Design</h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>Download a full PDF report or share your design summary</p>
            </div>
            <button onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}>
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Summary Card Preview */}
          <div className="p-6">
            <div
              ref={cardRef}
              className={`${darkMode ? 'bg-gray-900' : 'bg-white'} rounded-xl overflow-hidden shadow-2xl border-2 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}
            >
              {/* Gradient Header */}
              <div className="bg-gradient-to-r from-teal-500 to-blue-500 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <i className="ri-building-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-xl">{projectTitle}</h4>
                    <p className="text-white/80 text-sm">Master Architect</p>
                  </div>
                </div>
              </div>

              {/* Style Thumbnail */}
              {selectedStyle && (
                <div className="relative w-full h-56 overflow-hidden">
                  <img src={getStyleThumbnail(selectedStyle)} alt={selectedStyle} className="w-full h-full object-cover" crossOrigin="anonymous" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <span className="px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full text-sm font-bold text-gray-900 shadow-lg">{selectedStyle} Style</span>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className={`p-6 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                {/* Configuration */}
                <div className="mb-6">
                  <h5 className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide mb-3`}>Configuration</h5>
                  <div className="grid grid-cols-3 gap-3">
                    <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-3 flex items-center gap-2`}>
                      <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-hotel-bed-line text-white text-sm"></i>
                      </div>
                      <div>
                        <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{floorPlanConfig.bedrooms}</div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Bedrooms</div>
                      </div>
                    </div>
                    <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-3 flex items-center gap-2`}>
                      <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-drop-line text-white text-sm"></i>
                      </div>
                      <div>
                        <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{floorPlanConfig.bathrooms}</div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Bathrooms</div>
                      </div>
                    </div>
                    <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-3 flex items-center gap-2`}>
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-ruler-line text-white text-sm"></i>
                      </div>
                      <div>
                        <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} leading-tight`}>{floorPlanConfig.area}</div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{floorPlanConfig.unit}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Room-by-Room Breakdown ── */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <h5 className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>Room Breakdown</h5>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-teal-900/40 text-teal-400' : 'bg-teal-50 text-teal-600'}`}>
                      {roomBreakdown.length} rooms
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {roomBreakdown.map((room, idx) => (
                      <div
                        key={idx}
                        className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'} border rounded-lg p-3 flex items-start gap-2.5`}
                      >
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${room.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <i className={`${room.icon} text-white text-xs`}></i>
                        </div>
                        <div className="min-w-0">
                          <div className={`text-xs font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'} leading-tight`}>{room.name}</div>
                          <div className={`text-xs font-bold text-teal-500 mt-0.5`}>{room.size}</div>
                          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'} mt-0.5 leading-tight line-clamp-2`}>{room.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Selected Extras */}
                {getSelectedExtras().length > 0 && (
                  <div className="mb-6">
                    <h5 className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide mb-3`}>Features</h5>
                    <div className="flex flex-wrap gap-2">
                      {getSelectedExtras().map((extra: string, idx: number) => (
                        <span key={idx} className={`px-3 py-1.5 ${darkMode ? 'bg-teal-900/30 text-teal-400 border-teal-500/30' : 'bg-teal-50 text-teal-700 border-teal-200'} border rounded-full text-sm font-medium`}>
                          {extra}
                        </span>
                      ))}
                      {floorPlanConfig.extras.length > 5 && (
                        <span className={`px-3 py-1.5 ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'} rounded-full text-sm font-medium`}>
                          +{floorPlanConfig.extras.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Branding */}
                <div className={`pt-4 border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <i className="ri-building-line text-white text-sm"></i>
                    </div>
                    <div>
                      <div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Master Architect</div>
                      <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>AI-Powered Design</div>
                    </div>
                  </div>
                  <div className={`w-14 h-14 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                    <i className="ri-qr-code-line text-2xl text-gray-400"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`p-6 border-t ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'} flex gap-3 flex-wrap`}>
            {/* Download PDF */}
            <button
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf}
              className="flex-1 px-5 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-lg font-semibold transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloadingPdf ? (
                <><i className="ri-loader-4-line animate-spin"></i>Generating PDF...</>
              ) : (
                <><i className="ri-file-pdf-line"></i>Download PDF</>
              )}
            </button>

            {/* Print-Ready Mode */}
            <button
              onClick={() => setShowPrintReady(true)}
              className={`flex-1 px-5 py-3 ${darkMode ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white'} rounded-lg font-semibold transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer`}
            >
              <i className="ri-printer-line"></i>Print-Ready
            </button>

            {/* Download Image */}
            <button
              onClick={handleDownloadImage}
              disabled={isDownloadingImg}
              className={`flex-1 px-5 py-3 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white border-gray-600' : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'} border rounded-lg font-semibold transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isDownloadingImg ? (
                <><i className="ri-loader-4-line animate-spin"></i>Generating...</>
              ) : (
                <><i className="ri-image-line"></i>Save as Image</>
              )}
            </button>

            {/* Copy Link */}
            <button
              onClick={() => { onCopyLink(); onClose(); }}
              className={`flex-1 px-5 py-3 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white border-gray-600' : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'} border rounded-lg font-semibold transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer`}
            >
              <i className="ri-links-line"></i>Copy Link
            </button>
          </div>
        </div>
      </div>

      {/* Print-Ready Modal */}
      {showPrintReady && (
        <PrintReadyModal
          isOpen={showPrintReady}
          onClose={() => setShowPrintReady(false)}
          floorPlanData={{
            style: selectedStyle,
            bedrooms: floorPlanConfig.bedrooms,
            bathrooms: floorPlanConfig.bathrooms,
            area: floorPlanConfig.area,
            kitchenType: floorPlanConfig.kitchenType,
            extras: floorPlanConfig.extras || []
          }}
        />
      )}
    </>
  );
};

export default ShareDesignModal;
